'use client';
import { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ConfirmDialog from 'popup/ConfirmDialog';
import { Search, Timer, Trash } from 'lucide-react';
import {
	CourseGetPaging,
	CourseGetTeachingAndVehicle,
} from '../services/courseService';
import { toast } from 'react-hot-toast';
import LoadingOverlay from './Static/LoadingOverlay';
import moment from 'moment';
import { Course, TeachingAndVehicle } from '../models/model';
import { useParams } from 'react-router-dom';

interface SelectableItem {
	id: string;
	name: string;
	licenseClassName?: string;
}

interface BulkSelectModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	items: SelectableItem[];
	itemNone?: SelectableItem[] | null;
	onSubmit: (selectedIds: string[]) => void;
	onDelete: (id: string) => Promise<void>;
	selectedIds: string[];
	showLicenseClass?: boolean;
}

const BulkSelectModal = ({
	isOpen,
	onClose,
	title,
	items,
	itemNone = null,
	onSubmit,
	onDelete,
	selectedIds: initialSelectedIds,
	showLicenseClass = false,
}: BulkSelectModalProps) => {
	const { courseId }: any = useParams();
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedIds, setSelectedIds] =
		useState<string[]>(initialSelectedIds);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteId, setDeleteId] = useState<string | null>(null);
	const [activeTab, setActiveTab] = useState<
		'qualified' | 'unqualified' | 'course'
	>('qualified');
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingSubmit, setPendingSubmit] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [courses, setCourses] = useState<TeachingAndVehicle[]>([]);
	const [courseSelected, setCourseSelected] = useState<string>('');
	const [courseFilter, setCourseFilter] = useState<TeachingAndVehicle>({
		courseCode: '',
		courseId: '',
		courseName: '',
		licenseClassName: '',
		teachers: [],
		vehicles: [],
	});

	useEffect(() => {
		fetchCourse();
	}, [courseId, isOpen]);
	// Reset selectedIds when initialSelectedIds changes
	useEffect(() => {
		setSelectedIds(initialSelectedIds);
	}, [initialSelectedIds]);

	// Get current items based on active tab
	const getCurrentItems = () => {
		if (itemNone === null) return items;
		return activeTab === 'qualified' ? items : itemNone;
	};

	// Filter items based on searchTerm
	const filteredItems = getCurrentItems().filter((item) => {
		const searchLower = searchTerm.toLowerCase();
		return (
			item.name.toLowerCase().includes(searchLower) ||
			(showLicenseClass &&
				item.licenseClassName?.toLowerCase().includes(searchLower))
		);
	});

	const handleItemToggle = (id: string) => {
		setSelectedIds((prev) =>
			prev.includes(id)
				? prev.filter((itemId) => itemId !== id)
				: [...prev, id]
		);
	};

	function modalIsCar() {
		return title.toLowerCase().search('xe') > 0;
	}

	const handleSelectAll = () => {
		if (activeTab == 'course') {
			if (modalIsCar()) {
				if (selectedIds.length === courseFilter.vehicles.length) {
					setSelectedIds([]);
				} else {
					setSelectedIds(
						courseFilter.vehicles.map((item) => item.id)
					);
				}
			} else {
				if (selectedIds.length === courseFilter.teachers.length) {
					setSelectedIds([]);
				} else {
					setSelectedIds(
						courseFilter.teachers.map((item) => item.id)
					);
				}
			}
		} else {
			if (selectedIds.length === filteredItems.length) {
				setSelectedIds([]);
			} else {
				setSelectedIds(filteredItems.map((item) => item.id));
			}
		}
	};

	const handleSubmit = () => {
		if (
			itemNone &&
			selectedIds.some((id) => itemNone.some((item) => item.id === id))
		) {
			// Some selected items are from the unqualified list
			setPendingSubmit(true);
			setShowConfirmDialog(true);
		} else {
			onSubmit(selectedIds);
			onClose();
		}
	};

	const handleConfirmSubmit = () => {
		setShowConfirmDialog(false);
		onSubmit(selectedIds);
		onClose();
	};

	const handleDelete = async (id: string) => {
		setDeleteId(id);
		setIsDeleting(true);
		try {
			await onDelete(id);
			setSelectedIds((prev) => prev.filter((itemId) => itemId !== id));
		} catch (error) {
			console.error('Delete failed:', error);
		} finally {
			setIsDeleting(false);
			setDeleteId(null);
		}
	};

	async function fetchCourse() {
		setIsLoading(true);
		try {
			const res = await CourseGetTeachingAndVehicle(courseId);
			setCourses(res.result);
			if (courses.length > 0) {
				console.log(courses[0].courseId);
				setCourseSelected(courses[0].courseId);
			}
		} catch (error) {
			toast.error(
				'Lỗi khi lấy danh sách học viên và xe của khóa học liên quan: ' +
					error.message
			);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		setCourseFilter(
			courses.find((item) => item.courseId === courseSelected)
		);
		setSelectedIds([]);
	}, [courseSelected]);
	return (
		<>
			<Transition appear show={isOpen} as={Fragment}>
				<Dialog as='div' className='relative z-50' onClose={onClose}>
					<Transition.Child
						as={Fragment}
						enter='ease-out duration-300'
						enterFrom='opacity-0'
						enterTo='opacity-100'
						leave='ease-in duration-200'
						leaveFrom='opacity-100'
						leaveTo='opacity-0'>
						<div className='fixed inset-0 bg-black/50 backdrop-blur-sm' />
					</Transition.Child>

					<div className='fixed inset-0 overflow-y-auto'>
						<div className='flex min-h-full items-center justify-center p-4 text-center'>
							<Transition.Child
								as={Fragment}
								enter='ease-out duration-300'
								enterFrom='opacity-0 scale-95'
								enterTo='opacity-100 scale-100'
								leave='ease-in duration-200'
								leaveFrom='opacity-100 scale-100'
								leaveTo='opacity-0 scale-95'>
								<Dialog.Panel className='w-full max-w-2xl transform overflow-hidden rounded-xl bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]'>
									<div className='p-4 border-b border-gray-200 flex justify-between items-center'>
										<Dialog.Title
											as='h3'
											className='text-lg font-semibold leading-6 text-gray-900'>
											{title}
										</Dialog.Title>
										<button
											onClick={onClose}
											className='text-gray-500 hover:text-gray-700 focus:outline-none'>
											<Timer />
										</button>
									</div>

									{/* Tabs */}
									{itemNone !== null && (
										<div className='border-b border-gray-200'>
											<nav className='flex'>
												<button
													onClick={() =>
														setActiveTab(
															'qualified'
														)
													}
													className={`px-4 py-2 text-sm font-medium ${
														activeTab ===
														'qualified'
															? 'text-blue-600 border-b-2 border-blue-600'
															: 'text-gray-500 hover:text-gray-700'
													}`}>
													Có sẳn
												</button>
												<button
													onClick={() =>
														setActiveTab(
															'unqualified'
														)
													}
													className={`px-4 py-2 text-sm font-medium ${
														activeTab ===
														'unqualified'
															? 'text-blue-600 border-b-2 border-blue-600'
															: 'text-gray-500 hover:text-gray-700'
													}`}>
													Bổ sung
												</button>
												<button
													onClick={() =>
														setActiveTab('course')
													}
													className={`px-4 py-2 text-sm font-medium ${
														activeTab === 'course'
															? 'text-blue-600 border-b-2 border-blue-600'
															: 'text-gray-500 hover:text-gray-700'
													}`}>
													Khóa học
												</button>
											</nav>
										</div>
									)}

									{activeTab == 'course' ? (
										<div className='px-7 py-2'>
											<label className='text-xs text-gray-500 block'>
												Khóa học đề xuất:{' '}
											</label>
											<div className='md:flex md:items-center mb-3 md:mr-4'>
												<select
													className='md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none'
													value={courseSelected}
													onChange={(e) =>
														setCourseSelected(
															e.target.value
														)
													}>
													{courses.map((lc) => (
														<option
															key={lc.courseId}
															value={lc.courseId}>
															{lc.courseName}
														</option>
													))}
												</select>
											</div>
										</div>
									) : (
										<div className='p-4 border-b border-gray-200'>
											<div className='relative'>
												<input
													type='text'
													placeholder='Tìm kiếm...'
													className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
													value={searchTerm}
													onChange={(e) =>
														setSearchTerm(
															e.target.value
														)
													}
												/>
												<Search className='absolute left-3 top-3 text-gray-400' />
											</div>
										</div>
									)}

									{/* Select all checkbox */}
									<div className='px-4 py-2 border-b border-gray-200'>
										<label className='inline-flex items-center'>
											<input
												type='checkbox'
												checked={
													filteredItems.length > 0 &&
													selectedIds.length ===
														filteredItems.length
												}
												onChange={handleSelectAll}
												className='form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500'
											/>
											<span className='ml-2 text-gray-700'>
												Chọn tất cả
											</span>
										</label>
									</div>

									{/* Items list */}
									<div className='flex-1 overflow-y-auto'>
										{filteredItems.length === 0 &&
										courses.length == 0 ? (
											<div className='p-4 text-center text-gray-500'>
												Không tìm thấy kết quả phù hợp
											</div>
										) : (
											<ul className='divide-y divide-gray-200'>
												{activeTab == 'course' ? (
													courseSelected ? (
														modalIsCar() ? (
															courseFilter
																.vehicles
																.length > 0 ? (
																courseFilter.vehicles.map(
																	(
																		vehicle
																	) => (
																		<li
																			key={
																				vehicle.id
																			}
																			className='px-4 py-3 hover:bg-gray-50'>
																			<div className='flex items-center justify-between'>
																				<div className='flex items-center'>
																					<input
																						type='checkbox'
																						checked={selectedIds.includes(
																							vehicle.id
																						)}
																						onChange={() =>
																							handleItemToggle(
																								vehicle.id
																							)
																						}
																						className='form-checkbox h-4 w-4 text-blue-600 rounded mr-3 focus:ring-blue-500'
																					/>
																					<div>
																						<p className='font-medium text-gray-900'>
																							{
																								vehicle.plateNumber
																							}
																						</p>
																						<p className='text-sm text-gray-500'>
																							{
																								vehicle.licenseClassName
																							}
																						</p>
																					</div>
																				</div>
																				<button
																					onClick={() =>
																						handleDelete(
																							vehicle.id
																						)
																					}
																					disabled={
																						isDeleting &&
																						deleteId ===
																							vehicle.id
																					}
																					className='text-red-600 hover:text-red-900 disabled:opacity-50 focus:outline-none'>
																					{isDeleting &&
																					deleteId ===
																						vehicle.id ? (
																						'Đang xóa...'
																					) : (
																						<Trash />
																					)}
																				</button>
																			</div>
																		</li>
																	)
																)
															) : (
																<div
																	className={
																		'text-center font-medium text-sm'
																	}>
																	Khóa học
																	không có xe
																	khả dụng!
																</div>
															)
														) : courseFilter
																.teachers
																.length > 0 ? (
															courseFilter.teachers.map(
																(teacher) => (
																	<li
																		key={
																			teacher.id
																		}
																		className='px-4 py-3 hover:bg-gray-50'>
																		<div className='flex items-center justify-between'>
																			<div className='flex items-center'>
																				<input
																					type='checkbox'
																					checked={selectedIds.includes(
																						teacher.id
																					)}
																					onChange={() =>
																						handleItemToggle(
																							teacher.id
																						)
																					}
																					className='form-checkbox h-4 w-4 text-blue-600 rounded mr-3 focus:ring-blue-500'
																				/>
																				<div>
																					<p className='font-medium text-gray-900'>
																						{
																							teacher.fullName
																						}
																					</p>
																					<p className='text-sm text-gray-500'>
																						{
																							teacher.licenseClassName
																						}
																					</p>
																				</div>
																			</div>
																			<button
																				onClick={() =>
																					handleDelete(
																						teacher.id
																					)
																				}
																				disabled={
																					isDeleting &&
																					deleteId ===
																						teacher.id
																				}
																				className='text-red-600 hover:text-red-900 disabled:opacity-50 focus:outline-none'>
																				{isDeleting &&
																				deleteId ===
																					teacher.id ? (
																					'Đang xóa...'
																				) : (
																					<Trash />
																				)}
																			</button>
																		</div>
																	</li>
																)
															)
														) : (
															<div
																className={
																	'text-center font-medium text-sm'
																}>
																Khóa học không
																có giáo viên khả
																dụng!
															</div>
														)
													) : (
														''
													)
												) : (
													filteredItems.map(
														(item) => (
															<li
																key={item.id}
																className='px-4 py-3 hover:bg-gray-50'>
																<div className='flex items-center justify-between'>
																	<div className='flex items-center'>
																		<input
																			type='checkbox'
																			checked={selectedIds.includes(
																				item.id
																			)}
																			onChange={() =>
																				handleItemToggle(
																					item.id
																				)
																			}
																			className='form-checkbox h-4 w-4 text-blue-600 rounded mr-3 focus:ring-blue-500'
																		/>
																		<div>
																			<p className='font-medium text-gray-900'>
																				{
																					item.name
																				}
																			</p>
																			{showLicenseClass &&
																				item.licenseClassName && (
																					<p className='text-sm text-gray-500'>
																						{
																							item.licenseClassName
																						}
																					</p>
																				)}
																		</div>
																	</div>
																	<button
																		onClick={() =>
																			handleDelete(
																				item.id
																			)
																		}
																		disabled={
																			isDeleting &&
																			deleteId ===
																				item.id
																		}
																		className='text-red-600 hover:text-red-900 disabled:opacity-50 focus:outline-none'>
																		{isDeleting &&
																		deleteId ===
																			item.id ? (
																			'Đang xóa...'
																		) : (
																			<Trash />
																		)}
																	</button>
																</div>
															</li>
														)
													)
												)}
											</ul>
										)}
									</div>

									{/* Footer buttons */}
									<div className='p-4 border-t border-gray-200 flex justify-end space-x-3'>
										<button
											onClick={onClose}
											className='px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500'>
											Hủy
										</button>
										<button
											onClick={handleSubmit}
											disabled={selectedIds.length === 0}
											className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'>
											Lưu
										</button>
									</div>
								</Dialog.Panel>
							</Transition.Child>
						</div>
					</div>
				</Dialog>
			</Transition>
			{isLoading && <LoadingOverlay message='Đang tải dữ liệu...' />}

			{/* Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showConfirmDialog}
				title={`Xác nhận thêm ${modalIsCar() ? 'xe' : ' giáo viên'}`}
				description={`Bạn có chắc chắn muốn thêm những ${
					modalIsCar() ? 'xe' : ' giáo viên'
				} đang thuộc khóa khác?`}
				onClose={() => setShowConfirmDialog(false)}
				onConfirm={handleConfirmSubmit}
			/>
		</>
	);
};

export default BulkSelectModal;
